import { Avatar, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Popover, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { Restore as RestoreIcon, Visibility as VisibilityIcon, Edit as EditIcon, Delete as DeleteIcon, } from "@mui/icons-material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import moment from "moment";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { trackActivity } from "../../../utils/mixpanel";
import UploadingIcon from "../../../shared_elements/components/UploadingIcon";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AuditPopover from "../../../shared_elements/components/popover";
import { LessMoreComp } from '../../../shared_elements/components';
import CustomLessMoreComp from '../../../shared_elements/components/CustomLessMoreComp';
import { PermissionWrapper } from "../../../wrappers";
import { useSelector } from "react-redux";
import { dateMonthYearDateFormat } from "../../../constants";

const statusMap = {
  draft: {
    label: "Draft",
    color: "#89bfff",
    backgroundImage: "linear-gradient(90deg,#bedbfd,#dcebfc 95%)",
  },
  active: {
    label: "Active",
    color: "#5ec264",
    backgroundImage: "linear-gradient(90deg,#beed87,#d2e9b6 95%)",
  },
  closed: {
    label: "Closed",
    color: "#d32f2f",
    backgroundImage: "linear-gradient(269deg,#fcd5d5,#fca8a8 95%)",
  },
  terminated: {
    label: "Terminated",
    color: "#ff9800",
    backgroundImage: "linear-gradient(269deg,#fadfb8,#fdd79e 95%)",
  },
  "on-hold": {
    label: "On-hold",
    color: "#ee7c26",
    backgroundImage: "linear-gradient(269deg,#ffdfcb,#ffcdad 95%)",
  },
};

const LeaseLabel = ({ label, statusChange, isStatusChangeAllowed = true }) => {
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Lease Listing`, page_type: page_type, })
  }
  const statusKey = label?.toLowerCase?.();
  const status = statusMap[statusKey] || { label: label || "-", color: "#000" };
  const handleButtonClick = () => {
    if (!isStatusChangeAllowed) return;
    statusChange();
    handleItemClicked('Lease Status Change Button')
  };
  return (
    <Button
      variant="contained"
      onClick={handleButtonClick}
      disabled={!isStatusChangeAllowed}
      endIcon={isStatusChangeAllowed ? <ArrowDropDownIcon /> : null}
      sx={{
        backgroundImage: status.backgroundImage,
        color: status.color,
        textTransform: "capitalize",
        padding: "0 6px",
        fontWeight: "600",
        alignItems: "center",
        fontSize: "13px",
        whiteSpace: "nowrap",
        width: "110px",
        justifyContent: "center",
        cursor: isStatusChangeAllowed ? "pointer" : "not-allowed",
        "& .MuiButton-endIcon": {
          marginLeft: "0px",
        },
        "&.Mui-disabled": {
          cursor: "not-allowed",
          opacity: 1,
        },
      }}
    >
      {status.label}
    </Button>

  );
};

const LeaseList = ({ item, bulk, onEditFn, uploadedFileData, uploadFileProgressData, viewRevisonHistory, viewFileStatus, slug, onView, onDeleteEolFn, toggleBulkOps, statusChange, onOpenAttachments }) => {
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Leases Listing`, page_type: page_type, })
  }
  const leaseDetailUrl = `/lease/${item?.slug}/lease-details/headlines`;
  const handleRowLinkClick = (e) => {
    e.preventDefault();
    onView();
    handleItemClicked('Clicked on Table data');
  };
  const { permission } = useSelector((state) => state.leases);
  const leasePermission = permission?.permissions?.lease_revamp_application?.lease_revamp?.lease_details;
  const leaseStatus = Array.isArray(leasePermission) ? leasePermission.includes('U') : false;
  const assetTypeKeys = {
    1: 'aircraft_type',
    2: 'engine_type',
    3: 'apu_type'
  }
  const assetTypeName = item?.[assetTypeKeys[item?.asset_type]]?.name ?? null;

  return (
    <>
      <TableRow>
        <TableCell className="table-actions-list">
          <Box className="table-icon-container">
            <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease_details", operationType: ['R'] }}>
              <Checkbox style={{ verticalAlign: 'super' }} color="primary" size='small' checked={bulk?.ids.find(items => items?.id === item?.id) ? true : false} onChange={toggleBulkOps} />
            </PermissionWrapper>
            <AuditPopover item={item}>
              <MoreVertIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }} />
            </AuditPopover>
            <Tooltip title="Revision History"><RestoreIcon className="revision-history" sx={{ cursor: 'pointer' }} onClick={() => { viewRevisonHistory(); handleItemClicked('RevisionHistoryIcon') }} fontSize='small' color='primary' /></Tooltip>
            <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease_details", operationType: ['R'] }}>
              <Tooltip title="View"><VisibilityIcon sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('ViewIcon') }} fontSize='small' color='primary' /></Tooltip>
            </PermissionWrapper>
            <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease_details", operationType: ['U'] }}>
              <Tooltip title="Edit"><EditIcon sx={{ cursor: 'pointer' }} onClick={() => { onEditFn(); handleItemClicked('EditIcon') }} fontSize='small' color='primary' /></Tooltip>
            </PermissionWrapper>
            {
              item.status === "Draft" && <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease_details", operationType: ['D'] }}><Tooltip title="Delete"><DeleteIcon sx={{ cursor: 'pointer' }} onClick={() => { onDeleteEolFn(); handleItemClicked('DeleteIcon') }} fontSize='small' color='error' /></Tooltip></PermissionWrapper>
            }
            <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "lease_details", operationType: ['R'] }}>
              {
                item?.attachments?.length > 0 && (
                  <Tooltip title="Attached File(s)">
                    <Box className="attachment-container">
                      <AttachFileIcon fontSize="small" color="primary" onClick={() => { onOpenAttachments(); handleItemClicked('AttachmentsIcon') }} sx={{ cursor: "pointer" }} />
                      <Box className="attachment-count"> ({item.attachments.length}) </Box>
                    </Box>
                  </Tooltip>
                )
              }
            </PermissionWrapper>
            {(slug === item?.slug) && <UploadingIcon viewFileStatus={() => viewFileStatus(uploadedFileData)} />}
          </Box>
        </TableCell>
        <TableCell sx={{cursor: 'pointer'}}>
          <LeaseLabel
            label={item?.status}
            statusChange={() => statusChange(item)}
            isStatusChangeAllowed={leaseStatus}
          />
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", cursor: 'pointer' }}><a href={leaseDetailUrl} onClick={handleRowLinkClick} style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: 'pointer' }}><div style={{ width: item?.reference_code?.length > 50 ? "300px" : '' }}><LessMoreComp name={item?.reference_code} lessMr='reference_code' /></div></a></TableCell>
        <TableCell sx={{ minWidth: '200px', cursor: 'pointer' }}><a href={leaseDetailUrl} onClick={handleRowLinkClick} style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: 'pointer' }}><div style={{ width: item?.assigned_code?.length > 50 ? "300px" : '' }}><LessMoreComp name={item?.assigned_code} lessMr='assigned_code' /></div></a></TableCell>
        <TableCell sx={{ minWidth: '200px', cursor: 'pointer' }}>
          <a href={leaseDetailUrl} onClick={handleRowLinkClick} style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: 'pointer' }}>
            <div style={{ width: item?.asset?.name?.length > 50 ? "300px" : '' }}>
              {assetTypeName ? <p style={{ margin: 0, fontSize: '12px' }}>{assetTypeName}</p> : null}
              <Typography component="div" sx={{ m: 0, fontSize: '12px', fontFamily: 'Roboto-Medium !important' }}> {item?.asset?.name || '--'} </Typography>
            </div>
          </a></TableCell>
        <TableCell sx={{ minWidth: '200px', cursor: 'pointer' }}><a href={leaseDetailUrl} onClick={handleRowLinkClick} style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: 'pointer' }}><div style={{ width: item?.lessor_detail?.name?.length > 50 ? "300px" : '' }}><LessMoreComp name={item?.lessor_detail?.name} lessMr='lessor_detail_name' /></div></a></TableCell>
        <TableCell sx={{ minWidth: '200px', cursor: 'pointer' }}><a href={leaseDetailUrl} onClick={handleRowLinkClick} style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: 'pointer' }}><div style={{ width: item?.owner?.name?.length > 50 ? "300px" : '' }}><LessMoreComp name={item?.owner?.name} lessMr='owner_name' /></div></a></TableCell>
        <TableCell sx={{ minWidth: "200px", cursor: 'pointer' }}>
          <a href={leaseDetailUrl} onClick={handleRowLinkClick} style={{ textDecoration: "none", color: "inherit", display: "block", cursor: "pointer" }}>
            {item?.lessee?.logo ? (
              <>
                <Box className="lessee-logo-card">
                  <img src={item.lessee.logo} alt={item?.lessee?.name || "Lessee"} loading="lazy" className="lessee-logo-img" />
                </Box>
                <div style={{ width: item?.lessee?.name?.length > 50 ? "300px" : "" }}>
                  <CustomLessMoreComp name={item?.lessee?.name} lessMr='Lessee' />
                </div>
              </>
            ) : item?.lessee?.name ? (
              <div style={{ width: item.lessee.name.length > 50 ? "300px" : "" }}>
                <CustomLessMoreComp name={item?.lessee?.name} lessMr='Lessee' />
              </div>
            ) : (
              "--"
            )}
          </a>
        </TableCell>
        <TableCell sx={{ minWidth: '200px', whiteSpace: "nowrap", cursor: 'pointer' }}><a href={leaseDetailUrl} onClick={handleRowLinkClick} style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: 'pointer' }}>{item?.lease_type?.label || "-"}</a></TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", cursor: 'pointer' }}><a href={leaseDetailUrl} onClick={handleRowLinkClick} style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: 'pointer' }}>{`${moment(item.start_date).format(dateMonthYearDateFormat).toUpperCase()} - ${moment(item.end_date).format(dateMonthYearDateFormat).toUpperCase()}` || "--"}</a></TableCell>
      </TableRow>
    </>
  );
};

export default LeaseList;
